import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { softDelete } from '../../../lib/softDelete';
import { supabase } from '../../../lib/supabase';
import type { ProjectInput, ProjectRow, ProjectStatus } from './projects.schema';

export const projectsQueryKey = (orgId: string | null) => ['projects', orgId] as const;

function emptyToNull(value: string | undefined): string | null {
  const trimmed = value?.trim() ?? '';
  return trimmed.length > 0 ? trimmed : null;
}

interface ProjectsTableRowSelect {
  id: string;
  organization_id: string;
  name: string;
  status: string;
  company_id: string | null;
  product_line_id: string | null;
  quote_id: string | null;
  source_lead_id: string | null;
  primary_contact_id: string | null;
  value_amount: number;
  value_currency: string;
  hourly_rate: number | null;
  owned_by: string | null;
  started_at: string | null;
  expected_end_date: string | null;
  completed_at: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

function toProjectRow(row: ProjectsTableRowSelect): ProjectRow {
  return {
    ...row,
    status: row.status as ProjectRow['status'],
  };
}

const PROJECT_COLUMNS =
  'id, organization_id, name, status, company_id, product_line_id, quote_id, source_lead_id, primary_contact_id, value_amount, value_currency, hourly_rate, owned_by, started_at, expected_end_date, completed_at, notes, created_at, updated_at';

async function fetchProjects(orgId: string): Promise<ProjectRow[]> {
  const { data, error } = await supabase
    .from('projects')
    .select(PROJECT_COLUMNS)
    .eq('organization_id', orgId)
    .is('deleted_at', null)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data as ProjectsTableRowSelect[]).map(toProjectRow);
}

async function fetchProject(orgId: string, id: string): Promise<ProjectRow | null> {
  const { data, error } = await supabase
    .from('projects')
    .select(PROJECT_COLUMNS)
    .eq('organization_id', orgId)
    .eq('id', id)
    .is('deleted_at', null)
    .maybeSingle();
  if (error) throw error;
  return data ? toProjectRow(data) : null;
}

/** Keyed under ['projects', orgId], so every project mutation refreshes it. */
export function useProject(orgId: string | null, id: string | null) {
  return useQuery({
    queryKey: [...projectsQueryKey(orgId), 'detail', id] as const,
    queryFn: () => {
      if (!orgId || !id) return Promise.resolve(null);
      return fetchProject(orgId, id);
    },
    enabled: Boolean(orgId && id),
    staleTime: 15 * 1000,
  });
}

export function useProjects(orgId: string | null) {
  return useQuery({
    queryKey: projectsQueryKey(orgId),
    queryFn: () => {
      if (!orgId) return Promise.resolve([]);
      return fetchProjects(orgId);
    },
    enabled: Boolean(orgId),
    staleTime: 30 * 1000,
  });
}

interface ProjectInsertPayload {
  organization_id: string;
  name: string;
  status: string;
  company_id: string | null;
  product_line_id: string | null;
  value_amount: number;
  hourly_rate: number | null;
  started_at: string | null;
  expected_end_date: string | null;
  notes: string | null;
  owned_by: string;
  created_by: string;
}

interface ProjectUpdatePayload {
  name: string;
  status?: string;
  company_id: string | null;
  product_line_id: string | null;
  value_amount: number;
  hourly_rate: number | null;
  started_at: string | null;
  expected_end_date: string | null;
  notes: string | null;
}

function toPayload(values: ProjectInput): ProjectUpdatePayload {
  return {
    name: values.name.trim(),
    company_id: emptyToNull(values.company_id),
    // A line only makes sense with its customer; 0026 enforces the pairing.
    product_line_id: emptyToNull(values.company_id) ? emptyToNull(values.product_line_id) : null,
    value_amount: typeof values.value_amount === 'number' ? values.value_amount : 0,
    hourly_rate: typeof values.hourly_rate === 'number' ? values.hourly_rate : null,
    started_at: emptyToNull(values.started_at),
    expected_end_date: emptyToNull(values.expected_end_date),
    notes: emptyToNull(values.notes),
  };
}

export function useUpsertProject(orgId: string | null, userId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, values }: { id: string | null; values: ProjectInput }) => {
      if (!orgId || !userId) throw new Error('Missing project context');

      if (id) {
        // Status is not written here on edit: it only moves through
        // useChangeProjectStatus, which enforces the lifecycle table.
        const payload: ProjectUpdatePayload = toPayload(values);
        const { error } = await supabase
          .from('projects')
          .update(payload)
          .eq('id', id)
          .eq('organization_id', orgId);
        if (error) throw error;
        return;
      }

      const payload: ProjectInsertPayload = {
        ...toPayload(values),
        status: values.status,
        organization_id: orgId,
        owned_by: userId,
        created_by: userId,
      };
      const { error } = await supabase.from('projects').insert(payload);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['projects', orgId] });
      // The customer page and the company drawer read projects through their own keys.
      await queryClient.invalidateQueries({ queryKey: ['customer-profile'] });
      await queryClient.invalidateQueries({ queryKey: ['company-deals', orgId] });
    },
  });
}

interface EntityActionError {
  error?: string;
  from?: string;
  to?: string;
  fields?: string[];
}

/**
 * Status changes go through the entity-action Edge Function, the single
 * authority for lifecycle transitions: it rejects illegal moves (409) and
 * writes the timeline activity in the same RPC as the update.
 */
export function useChangeProjectStatus(orgId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, to }: { id: string; to: ProjectStatus }) => {
      const result = await supabase.functions.invoke<{ success?: boolean }>('entity-action', {
        body: { action: 'change_status', entity_type: 'project', entity_id: id, to_status: to },
      });
      if (result.error) {
        let detail = result.error instanceof Error ? result.error.message : 'entity-action failed';
        const context: unknown = (result.error as { context?: unknown }).context;
        if (context instanceof Response) {
          const body = (await context.json().catch(() => null)) as EntityActionError | null;
          if (body?.error) detail = body.error;
        }
        throw new Error(detail);
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: projectsQueryKey(orgId) });
      // The customer page and the company drawer read projects through their own keys.
      await queryClient.invalidateQueries({ queryKey: ['customer-profile'] });
      await queryClient.invalidateQueries({ queryKey: ['company-deals', orgId] });
    },
  });
}

export function useSoftDeleteProject(orgId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      if (!orgId) throw new Error('Missing org context');
      await softDelete('projects', id);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['projects', orgId] });
      // The customer page and the company drawer read projects through their own keys.
      await queryClient.invalidateQueries({ queryKey: ['customer-profile'] });
      await queryClient.invalidateQueries({ queryKey: ['company-deals', orgId] });
    },
  });
}
